import React, { FC } from 'react'

import { styled } from '@linaria/react'
import { FormattedMessage } from 'react-intl'

import { AcceptMessage } from 'components/block/Footer/AcceptMessage'
import { Copyright } from 'components/block/Footer/Copyright'
import { RecommendationTechnologiesMessage } from 'components/block/Footer/RecommendationTechnologiesMessage'
import { SupportLink } from 'components/block/Footer/SupportLink'
import { Anchor } from 'components/designSystem/Link/Anchor'
import { RouterLink } from 'components/designSystem/Link/RouterLink'
import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { Typography } from 'components/designSystem/Typography/Typography'
import {
  cityListPath,
  shortcutListPath,
} from 'components/page/Boarding/boarding.paths'
import {
  useShowCitiesLink,
  useShowCountries,
} from 'components/page/Boarding/footer/footer.hooks'
import { blogPath } from 'components/presentational/Navigation/Navigation.constants'
import { useShowLinkBlog } from 'hooks/useShowLinkBlog'

import appGallery from './app_gallery.svg'
import appStore from './app_store.svg'
import footerBackground from './footer_background.png'
import googlePlay from './google_play.svg'
import footerSquareLogo from './mamba_v2.svg'
import telegramButton from './telegram_button.svg'
import vkButton from './vk_button.svg'
import {
  appGalleryLink,
  appStoreLink,
  desktopWidth,
  googlePlayLink,
  mobileWidth,
  telegramLink,
  vkLink,
} from '../WaveBoarding.constants'

export const Footer: FC = () => {
  const isShowCityLink = useShowCitiesLink()
  const isShowCountries = useShowCountries()
  const hasLinkBlog = useShowLinkBlog()

  return (
    <Wrapper style={{ backgroundImage: `url(${footerBackground})` }}>
      <Container>
        <SquareLogo
          src={footerSquareLogo}
          alt="Footer Logo"
          style={{ gridArea: 'square-logo' }}
          loading="lazy"
        />

        <StoreBlock style={{ gridArea: 'stores' }}>
          <StoreList>
            <a href={appStoreLink} data-name="app-store-action">
              <img src={appStore} alt="App Store" loading="lazy" />
            </a>
            <a href={googlePlayLink} data-name="google-play-action">
              <img src={googlePlay} alt="Google Play" loading="lazy" />
            </a>
            <a href={appGalleryLink} data-name="app-gallery-action">
              <img src={appGallery} alt="App Gallery" loading="lazy" />
            </a>
          </StoreList>
          <Typography fontSize={11} style={{ maxWidth: 367 }}>
            <FormattedMessage
              id="footer.legal"
              defaultMessage="Apple и логотип Apple являются товарными знаками компании Apple Inc. Google Play и логотип Google Play являются товарными знаками компании Google LLC."
            />
          </Typography>
        </StoreBlock>

        <LinkList style={{ gridArea: 'link-list' }}>
          {hasLinkBlog && (
            <Anchor href={blogPath} data-name="blog-action">
              <Typography fontSize={16}>
                <FormattedMessage id="app.datebook" defaultMessage="Дейтбук" />
              </Typography>
            </Anchor>
          )}
          {isShowCityLink && (
            <RouterLink to={cityListPath} data-name="city-action">
              <Typography fontSize={16}>
                <FormattedMessage
                  id="boarding.footer.cities"
                  defaultMessage="Города"
                />
              </Typography>
            </RouterLink>
          )}
          {isShowCountries && (
            <RouterLink to={shortcutListPath} data-name="country-action">
              <Typography fontSize={16}>
                <FormattedMessage
                  id="boarding.footer.countries"
                  defaultMessage="Страны"
                />
              </Typography>
            </RouterLink>
          )}
        </LinkList>

        <Social style={{ gridArea: 'social' }}>
          <a href={telegramLink} data-name="telegram-action">
            <img
              src={telegramButton}
              width={40}
              height={40}
              alt="Telegram Button"
              loading="lazy"
            />
          </a>
          <a href={vkLink} data-name="vk-action">
            <img
              src={vkButton}
              width={40}
              height={40}
              alt="VK Button"
              loading="lazy"
            />
          </a>
        </Social>

        <Note style={{ gridArea: 'note' }}>
          <NoteContent>
            <AcceptMessage>
              {(chunks) => <Typography fontSize={16}>{chunks}</Typography>}
            </AcceptMessage>

            <Typography fontSize={16}>
              <RecommendationTechnologiesMessage />
            </Typography>

            <Typography fontSize={16}>
              <SupportLink />
            </Typography>
          </NoteContent>
        </Note>

        <CopyrightContainer fontSize={16} style={{ gridArea: 'copy' }}>
          <Copyright />
        </CopyrightContainer>
      </Container>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 90px;
  background-repeat: no-repeat;
  background-position-y: bottom;
  background-size: cover;
  padding-top: 120px;
`

const Container = styled.footer`
  display: grid;
  grid-template-areas:
    'square-logo link-list'
    'stores social'
    'copy note';
  grid-gap: 32px 5%;
  padding-top: 92px;

  @media (min-width: ${breakpoints.mobile}px) {
    width: ${desktopWidth};
  }

  @media (max-width: ${breakpoints.mobile - 1}px) {
    width: ${mobileWidth};
    grid-template-areas:
      'square-logo'
      'link-list'
      'social'
      'stores'
      'note'
      'copy';
    padding: 0 72px;
    text-align: center;
  }
`

const StoreBlock = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: ${breakpoints.mobile - 1}px) {
    align-items: center;
  }
`

const StoreList = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 12px;
  gap: 6px;

  @media (max-width: ${breakpoints.mobile - 1}px) {
    flex-direction: column;
    max-width: 115px;
    align-items: center;
  }
`

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  text-align: end;
  gap: 8px;

  @media (max-width: ${breakpoints.mobile - 1}px) {
    text-align: center;
    display: none;
  }
`

const Social = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: end;

  @media (max-width: ${breakpoints.mobile - 1}px) {
    justify-content: center;
  }
`

const Note = styled.div`
  text-align: end;
  display: flex;
  flex-direction: column;
  align-items: end;

  @media (max-width: ${breakpoints.mobile - 1}px) {
    && {
      text-align: center;
    }
  }
`

const NoteContent = styled.div`
  max-width: 350px;

  & > * + * {
    margin-top: var(--spacing-8px);
  }

  @media (max-width: ${breakpoints.mobile - 1}px) {
    align-self: center;
  }
`

const CopyrightContainer = styled(Typography)`
  max-width: 433px;
  margin: 0 auto;
`

const SquareLogo = styled.img`
  @media (max-width: ${breakpoints.mobile - 1}px) {
    margin: 0 auto;
  }
`
